import { useMemo } from "react";
import { Container, Col, Row } from "reactstrap";
import { useQuery } from "@apollo/client";
import * as PageQL from "queries/page";
import * as SiteQL from "queries/site";
import { Link } from "react-router-dom";
import { List } from "@repoflow/components/list-cursor";
import PageSchema from "@common/schema/page/public";
import * as widgets from "widgets/register";
import * as Section from "@repoflow/components/section";
import { translationProgress } from "@common/schema/page/translation";
import { localeOf, usePageGroups } from "./groups";
import { LiveLink, useLiveHost } from "../live";
import { GroupPublishBadges, chipClassOf, chipNoteOf, publishStateOf } from "../published";

export const Sidebar = () => null;

export const Indice = () => null;

// Where the listing lives in the answer, and — the list's own `listTag` — where
// the cursor beside it does. One constant rather than two literals: `onData`
// reads `last` out of the same document the list reads its items from.
export const PATH = "viewer.account.sites.get.pages";
const LIST_PATH = `${PATH}.list`;

// Hoisted, and exported so the suite drives the field list this screen renders
// rather than a copy of it — the convention beside every other config in this
// app. A literal here would be a new object every render, rebuilding the widget
// tree beneath the list's SchemaProvider (P139).
//
// `locale` and `translationKey` are gone from it, and that is this change: a
// column of raw locale codes and a column of opaque group names were how the
// screen said "these three rows are one page" while still drawing three rows.
// The languages are ON the row now, as links, and the group they name is the
// row itself.
export const LIST_FIELDS = {
  "/Public/Page": {
    fields: ["title", "route", "createdAt"],
  },
};

// The languages one logical page exists in, each a link to that language's page.
//
// The whole cell, including the View button that was here before it: `content`
// renders into the row's ONE free cell, so the languages share it with the
// action. `View` opens the row's head — the page whose title and route the row
// shows — and each chip opens the sibling it names, which is the "and a way to
// reach a specific language" half of the job. A group of one draws one chip,
// which is the page's own language and is worth saying: it is what `<html lang>`
// will hold.
//
// The code on the chip is the RESOLVED one (`localeOf`), so a page carrying no
// locale of its own reads as the site's default rather than as a blank — that
// blank column is what the listing showed before. The tooltip is where the
// sibling's title and route go, since the chip itself has room for a code.
export const LanguageLinks = ({ base, site, host, page, members }) => (
  <>
    <Link className={"btn btn-primary"} to={`${base}/${page.pageId}/`}>
      View
    </Link>{" "}
    {/* The row's head, as a visitor gets it. Beside View rather than instead of
        it, because they are different questions — one opens the record, the
        other opens the page — and only one of them exists on a site with no
        domain attached yet. It is the HEAD's URL: the languages beside it are
        each a different route and each their own page, and a listing that
        offered five outward links per row would bury the one anybody wants. */}
    <LiveLink
      host={host}
      page={page}
      // The icon alone, styled as a link rather than a button: this is the one
      // action on the row that leaves the app, and a second button beside View
      // read as a second way to open the record. `aria-label` keeps the name
      // the specs and a screen reader find it by; the URL is in the title.
      className={"btn btn-link"}
      aria-label="Open"
    >
      <i className="fa fa-external-link" aria-hidden="true" />
    </LiveLink>{" "}
    {/* For the GROUP, not the head: the row is one logical page in every
        language it has, and the state of the one that heads it says nothing
        about the others. A Spanish draft with edits waiting sits behind an
        English page that is published and unchanged, and this is where that
        stopped being invisible — the badge's title names the languages. */}
    <GroupPublishBadges members={members} site={site} />
    {members.map((member) => {
      // How much of that language is still the prose it was translated FROM.
      //
      // A group has one structure and many contents (see
      // @common/schema/page/translation), so an element added in any language
      // arrives in the others carrying the source's text and marked. This is
      // where that becomes visible without opening anything: the chip for a
      // language with work left in it is warning-coloured, and the tooltip
      // says how much.
      //
      // Read off `member.elements`, which PageFragment already selects for
      // every row of this listing — no extra field and no extra query.
      const { pending, total } = translationProgress(member);
      const behind = pending > 0;
      // And whether what a visitor gets of that language is what the author
      // has: the chip takes the colour of the row badge that says the same
      // thing, so the two read as one signal. Translation work left wins the
      // colour when both apply — it is the one an author has to open the
      // page for — and the tooltip carries both.
      const state = publishStateOf(member);

      return (
        <Link
          key={member.pageId}
          className={`badge ${
            member.pageId === page.pageId
              ? "badge-primary"
              : behind
              ? "badge-warning"
              : chipClassOf(state) || "badge-light"
          } mr-1`}
          to={`${base}/${member.pageId}/`}
          title={`${member.title || "Untitled"} — ${member.route || "no route"}${
            member.locale ? "" : " (site default)"
          }${behind ? ` — ${total - pending} of ${total} translated` : ""}${chipNoteOf(state)}`}
        >
          {localeOf(site, member)}
          {/* aria-hidden, so the chip's accessible NAME stays the bare
              language code — it is how every test and every reader addresses
              this link. The dot is the colour-blind-safe half of the signal;
              the count is in the title, which is the link's description. */}
          {behind ? <span aria-hidden="true"> •</span> : null}
        </Link>
      );
    })}
  </>
);

// WHAT THIS SCREEN CANNOT PROMISE.
//
// The listing pages on a cursor and a translation group can span the boundary,
// so the languages on a row are the ones LOADED — a page whose German row is on
// the next page shows "en es" until that page arrives, and then shows "en es
// de". Three ways out of that were available and only one of them is this
// change:
//
//   - group server-side, which is the only one with no boundary at all, and is
//     an API change (a listing that returns groups, or a `translationKey`
//     index) rather than a screen;
//   - raise the page size until groups rarely split, which moves the boundary
//     rather than removing it and makes the first paint of a large site slower
//     to buy it;
//   - group what is loaded and SAY SO, which is this.
//
// Which leaves the screen honest about the difference between "this page has
// one language" and "I have only loaded one of them", and that is what this
// line is. It appears and disappears with the list's own "Load more", because
// both are drawn from the same cursor: once the server says there is nothing
// after this page, every group on screen is complete and the note is gone.
export const PARTIAL_NOTE =
  "Grouped across the pages loaded so far — a translation further down the " +
  "listing is not on its page's row yet. Load more to complete the groups.";

export const Content = ({ match: { params, params: { siteId } } }) => {
  const { base } = Section.useSectionContext();

  // The site, for its `defaultLocale` and the order it declares its languages
  // in — which language heads a group, and which order the chips read in.
  //
  // `cache-first` and no error handling, the same as the Translate screen and
  // the page editor: every screen above this one resolves the same query, so
  // this is a cache read on all but the coldest entry, and a site that has not
  // answered yet degrades to `localeOf`'s own fallback ("en", undeclared
  // order) rather than to a broken listing.
  const { data: siteData } = useQuery(SiteQL.get, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });

  const site = siteData ? siteData.viewer.account.sites.get : null;
  const { filter, membersOf, onData, hasMore } = usePageGroups(site, LIST_PATH);

  // The hostname the rows link out on, resolved ONCE for the listing rather
  // than per row: `content` renders a cell per page and a hook in there would
  // be one query subscription per row for one answer. "" when this site has no
  // verified domain, and then no row draws an outward link — see ../live.
  const host = useLiveHost(siteId);

  // Memoised on everything it closes over, for the reason LIST_FIELDS is
  // hoisted: `content` is a COMPONENT, and a new identity is a new component
  // type — React remounts the cell rather than updating it (P139/P144).
  const content = useMemo(
    () =>
      ({ item }) =>
        (
          <LanguageLinks
            base={base}
            site={site}
            host={host}
            page={item}
            members={membersOf(item)}
          />
        ),
    [base, site, host, membersOf]
  );

  return (
    <Container>
      <div className="mt-4"></div>

      <Row>
        <Col>
          <Link className={"btn btn-primary"} to={`${base}/new`}>
            <i className="fa fa-plus" /> Add
          </Link>{" "}
          {/* Beside Add because it IS an Add: where import is invoked decides
              what it does, and from the listing the file becomes a NEW,
              independent page or group of language variants. The update verb —
              putting an edited file back into the pages it came from — is
              Import on the page's own screen, beside the Export that made the
              file. */}
          <Link className={"btn btn-secondary"} to={`${base}/import`}>
            Import
          </Link>
        </Col>
      </Row>

      <div className="mt-4"></div>

      {hasMore && (
        <Row>
          <Col>
            <div className="text-muted small mb-2">{PARTIAL_NOTE}</div>
          </Col>
        </Row>
      )}

      <List
        path={PATH}
        query={PageQL.list}
        variables={params}
        schema={PageSchema}
        widgets={widgets}
        target={{ $ref: "/Public/Page" }}
        empty={"No pages yet."}
        onData={onData}
        filter={filter}
        config={LIST_FIELDS}
        content={content}
      />
    </Container>
  );
};
